import React, { useEffect, useState } from "react";

import "./price.less";

import priceBg from "@assets/images/official_website_new/price_bg.jpg";
import { SCROLL_BRAND_IMAGES } from "../../shared/image_assets";
import Iconfont from "@components/iconfont/iconfont";
import { queryByConCloud } from "@api/cloud_disk";

function assetUrl(imported) {
  if (imported == null) return "";
  if (typeof imported === "string") return imported;
  if (typeof imported === "object") {
    if (typeof imported.src === "string") return imported.src;
    const d = imported.default;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && typeof d.src === "string") return d.src;
  }
  return "";
}

export default function MobilePricePage({ onSubscribe }) {
  const [plans, setPlans] = useState([]);
  const [activePlanIndex, setActivePlanIndex] = useState(0);

  useEffect(() => {
    let active = true;

    queryByConCloud({})
      .then((res) => {
        if (!active || !res?.success) return;

        const packageList = Array.isArray(res.result) ? res.result : [];
        const freePlanIndex = packageList.findIndex(
          ({ packageType }) => packageType === "FREE"
        );
        setPlans(packageList);
        setActivePlanIndex(freePlanIndex >= 0 ? freePlanIndex : 0);
      })
      .catch(() => {
        if (active) {
          setPlans([]);
          setActivePlanIndex(0);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const plan = plans[activePlanIndex];
  const isFreePlan = plan?.packageType === "FREE";
  const benefits = (plan?.rightConfigs || []).filter(
    ({ description }) => description
  );
  const hasPrice = plan?.price != null && plan.price !== "";
  const hasDisplayPrice = plan?.displayPrice != null && plan.displayPrice !== "";

  /** 跨标签跳转：先加 .is-pressed 让按下视觉变化提交一帧，再 window.open；
   *  避免部分真机 touchend 立刻打开新标签、点击效果到回原页才能看到。 */
  const handleContact = (e) => {
    e.preventDefault();
    const target = e.currentTarget;
    target.classList.add("is-pressed");
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.open("https://v.wjx.cn/vm/PD6aaEY.aspx#", "_blank", "noopener,noreferrer");
      setTimeout(() => target.classList.remove("is-pressed"), 200);
    }));
  };
  const handleSubscribe = (e) => {
    e.preventDefault();
    if (typeof onSubscribe === "function") onSubscribe(e);
  };
  return (
    <div className="m-price-page">
      {/* —— Hero（Figma 160:3600，375×314） —— */}
      <section
        className="m-price-hero"
        style={{ "--mp-hero-bg": `url("${assetUrl(priceBg)}")` }}
      >
        <h1 className="m-price-hero__title">
          快速搭建您的
          <br />
          专属品牌空间
        </h1>
        <div className="m-price-hero__tags">
          <span className="m-price-hero__tag">
            <span className="m-price-hero__tag-text">零代码搭建品牌网站（个人/企业）</span>
          </span>
          <span className="m-price-hero__tag">
            <span className="m-price-hero__tag-text">作品集/产品册</span>
          </span>
          <span className="m-price-hero__tag">
            <span className="m-price-hero__tag-text">私域圈新商业工具</span>
          </span>
        </div>
        <a
          href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
          className="m-price-hero__cta"
          target="_blank"
          rel="noopener noreferrer"
        >
          立即获取方案
        </a>
      </section>

      {/* —— Tab 行（Figma 2122:5001，375×36） —— */}
      <nav className="m-price-tabs" aria-label="套餐切换">
        {plans.map((packageItem, index) => (
          <button
            key={packageItem.id || `${packageItem.packageType}-${index}`}
            type="button"
            className={`m-price-tabs__item${
              activePlanIndex === index ? " is-active" : ""
            }`}
            onClick={() => setActivePlanIndex(index)}
          >
            {packageItem.name}
          </button>
        ))}
      </nav>

      {/* —— 套餐卡（免费版 2408:2514 / 付费版 2122:5007） —— */}
      {plan && (
        <section className="m-price-cardwrap">
          <article
            className={`m-price-card ${
              isFreePlan ? "m-price-card--free" : "m-price-card--paid"
            }`}
          >
            <div className="m-price-card__top">
              <h2 className="m-price-card__title">{plan.name}</h2>
              <div className="m-price-card__summary">
                <div className="m-price-card__price">
                  <span className="m-price-card__price-main">
                    {isFreePlan ? "免费" : hasPrice ? `¥${plan.price}` : "--"}
                  </span>
                  {!isFreePlan && hasPrice && (
                    <span className="m-price-card__price-suffix">/年起</span>
                  )}
                  {!isFreePlan && hasDisplayPrice && (
                    <span className="m-price-card__original-price">
                      <span>¥</span>
                      <span className="with-line-through">{plan.displayPrice}</span>
                      <span>年起</span>
                    </span>
                  )}
                </div>
                {/* 免费卡的「核心基础服务」只作为套餐说明标签：保留按钮外观与 hover/按下反馈，
                    点击不再触发任何跳转（与 PC/Pad 一致）；付费卡「立即订阅」仍走 onSubscribe。 */}
                <button
                  type="button"
                  className={`m-price-card__cta ${
                    isFreePlan ? "m-price-card__cta--small" : "m-price-card__cta--full"
                  }`}
                  onClick={isFreePlan ? undefined : handleSubscribe}
                >
                  {isFreePlan ? "核心基础服务" : "立即订阅"}
                </button>
              </div>
            </div>
            <div className="m-price-card__divider" aria-hidden="true" />
            <ul className="m-price-card__features">
              {benefits.map((benefit, index) => (
                <li
                  key={benefit.rightId || `${plan.id}-benefit-${index}`}
                  className="m-price-card__feature"
                >
                  <span className="m-price-card__feature-icon">
                    <Iconfont type="icon-a-dagouxi" aria-hidden="true" />
                  </span>
                  <span className="m-price-card__feature-text">
                    {benefit.description}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        </section>
      )}

      {/* —— 联系我们（Figma 2195:1954，200×36） —— */}
      <section className="m-price-other">
        <a
          href="#"
          className="m-price-other__cta"
          onClick={handleContact}
        >
          联系我们
        </a>
      </section>

      {/* —— 3500+ 品牌墙（Figma 160:3693，375×163） —— */}
      <section className="m-price-brands">
        <div className="m-price-brands__title">
          10000+个人、3500+团队/企业用户正在使用不觉
        </div>
        <div className="m-price-brands__strip">
          <div className="m-price-brands__track">
            {[...SCROLL_BRAND_IMAGES, ...SCROLL_BRAND_IMAGES].map((src, i) => (
              <div key={`m-price-brand-${i}`} className="m-price-brands__item">
                <img src={assetUrl(src)} alt="" draggable={false} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
